<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { FairIcon } from '@/ui/primitives/FairIcon'
import {
  FAIR_VARIANT_COMPARE_MIN,
  type FairVariantCompareItem,
} from '@/ui/tokens/fair-variant-compare.data'
import { COMPARISON_ROUTE } from '@/router/routes'

interface FairVariantCompareTrayProps {
  items: FairVariantCompareItem[]
  /** Hide on compare page itself */
  visible?: boolean
}

const props = withDefaults(defineProps<FairVariantCompareTrayProps>(), {
  visible: true,
})

const emit = defineEmits<{
  clear: []
}>()

const { t } = useI18n()
const router = useRouter()

const expanded = ref(false)

const show = computed(
  () => props.visible && props.items.length > 0,
)

const canGo = computed(() => props.items.length >= FAIR_VARIANT_COMPARE_MIN)

watch(
  () => props.items.length,
  (count) => {
    if (count === 0) {
      expanded.value = false
    }
  },
)

function toggleExpanded() {
  expanded.value = !expanded.value
}

function handleCompare() {
  if (!canGo.value) {
    return
  }
  void router.push(COMPARISON_ROUTE)
}

function handleClear() {
  emit('clear')
  expanded.value = false
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fair-variant-compare-tray"
      :class="{ 'fair-variant-compare-tray--expanded': expanded }"
      role="complementary"
      :aria-label="t('fair_compare.tray_aria')"
    >
      <Transition name="fair-variant-compare-tray-panel">
        <div
          v-if="expanded"
          class="fair-variant-compare-tray__panel"
        >
          <button
            type="button"
            class="fair-variant-compare-tray__clear"
            :aria-label="t('fair_compare.clear')"
            @click="handleClear"
          >
            <FairIcon
              name="close"
              :size="14"
            />
          </button>

          <ul class="fair-variant-compare-tray__thumbs">
            <li
              v-for="item in items"
              :key="item.variantId"
              class="fair-variant-compare-tray__thumb"
            >
              <img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                :alt="item.title"
                loading="lazy"
                decoding="async"
              >
            </li>
          </ul>

          <button
            type="button"
            class="fair-variant-compare-tray__cta"
            :disabled="!canGo"
            :aria-label="t('fair_compare.compare_cta')"
            @click="handleCompare"
          >
            <FairIcon
              name="compare"
              :size="18"
            />
          </button>
        </div>
      </Transition>

      <button
        type="button"
        class="fair-variant-compare-tray__fab"
        :aria-expanded="expanded"
        :aria-label="
          expanded
            ? t('fair_compare.tray_collapse')
            : t('fair_compare.tray_expand')
        "
        @click="toggleExpanded"
      >
        <FairIcon
          name="compare"
          :size="20"
        />
        <span class="fair-variant-compare-tray__badge">
          {{ items.length }}
        </span>
      </button>
    </div>
  </Teleport>
</template>
